import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'
import { createCheckoutSession, purchaseCredits } from '../lib/api'
import { Check, Rocket, CreditCard, Info, Loader2 } from 'lucide-react'

export default function Plans() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, profile } = useAuthStore()
  // Map subscription_status to plan type — 'trial' counts as 'basic'
  const rawStatus = profile?.subscription_status || null
  const currentPlan = rawStatus === 'trial' ? 'basic' : rawStatus
  const [loadingPlan, setLoadingPlan] = useState(null)
  const [buyingCredits, setBuyingCredits] = useState(null)
  const [error, setError] = useState(null)
  const [canceled, setCanceled] = useState(false)

  useEffect(() => {
    document.title = 'Pricing Plans — Scam Protection Starting at $9.99 | ScamView'
  }, [])

  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      setCanceled(true)
    }
  }, [searchParams])

  const plans = [
    {
      name: 'Basic',
      price: '$9.99',
      period: '/month',
      planType: 'basic',
      priceId: import.meta.env.VITE_STRIPE_PRICE_ID_BASIC,
      features: [
        '300 credits/month included',
        'Email scanning with AI analysis',
        'Risk scoring (1-10)',
        'Last 7 scans in history',
        'Extra credits: 100 for $1',
        'Email support'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Premium',
      price: '$19.99',
      period: '/month',
      planType: 'premium',
      priceId: import.meta.env.VITE_STRIPE_PRICE_ID_PREMIUM,
      features: [
        'HireMail protected inbox — auto-scans recruiter emails',
        '500 credits/month included',
        'Everything in Basic, plus:',
        'Phone number intelligence',
        'Brand impersonation detection',
        'Link masking analysis',
        'Community reviews & reports',
        'Unlimited scan history',
        'Extra credits: 100 for $1',
        'Priority support'
      ],
      cta: 'Start Free Trial',
      popular: true
    }
  ]

  const handleCheckout = async (plan) => {
    setError(null)

    // Must be logged in to checkout



    setLoadingPlan(plan.planType)
    try {
      const { url } = await createCheckoutSession(plan.priceId, plan.planType)
      if (url) {
        window.location.href = url
      } else {
        setError('Failed to create checkout session. Please try again.')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoadingPlan(null)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Canceled Banner */}
        {canceled && (
          <div className="bg-amber-500/20 text-amber-400 p-4 rounded-lg mb-8 text-center border border-amber-500/30">
            Payment was canceled. You can try again when you're ready.
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-8 text-center border border-red-500/30">
            {error}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-16">
          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-300">
            Basic plan includes a 14-day free trial. Cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div id="plans" className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`card relative flex flex-col ${plan.popular ? 'ring-2 ring-white/20' : ''
                }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
              )}

              {!plan.popular && (
                <div className="bg-cyan-500/15 text-cyan-400 text-xs font-semibold px-3 py-1.5 rounded-lg text-center mb-4 border border-cyan-500/20">
                  Includes 14-Day Free Trial
                </div>
              )}

              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{plan.name}</h2>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-400">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCheckout(plan)}
                disabled={loadingPlan !== null || (user && currentPlan === plan.planType)}
                className={`w-full mt-auto flex items-center justify-center gap-2 ${user && currentPlan === plan.planType
                  ? 'bg-white/10 text-gray-400 border border-white/10 rounded-lg py-3 px-6 cursor-not-allowed'
                  : 'btn-primary'
                  }`}
              >
                {user && currentPlan === plan.planType
                  ? <><Check className="w-4 h-4" /> Current Plan</>
                  : loadingPlan === plan.planType
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout...</>
                    : <><Rocket className="w-4 h-4" /> {plan.planType === 'basic' ? 'Start Basic Plan' : 'Start Premium Plan'}</>}
              </button>
            </div>
          ))}
        </div>

        {/* Credit Purchase Tiers */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-8">
            <h2
              className="text-3xl font-bold text-white mb-2"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Need More Credits?
            </h2>
            <p className="text-gray-400">
              Purchase extra credits anytime. They never expire.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { qty: 1, credits: 100, price: '$1', popular: false },
              { qty: 2, credits: 200, price: '$2', popular: false },
              { qty: 5, credits: 500, price: '$5', popular: true },
            ].map((pack) => (
              <div
                key={pack.qty}
                className={`card text-center relative ${pack.popular ? 'ring-2 ring-emerald-500/30' : ''
                  }`}
              >
                {pack.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-black px-3 py-0.5 rounded-full text-xs font-semibold">
                    Best Value
                  </div>
                )}
                <p className="text-3xl font-bold text-emerald-400 mb-1">{pack.credits}</p>
                <p className="text-gray-400 text-sm mb-4">credits</p>
                <p className="text-2xl font-bold text-white mb-4">{pack.price}</p>
                <button
                  onClick={async () => {
                    if (!user) {
                      document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })
                      return
                    } // must subscribe first
                    setBuyingCredits(pack.qty)
                    setError(null)
                    try {
                      const { url } = await purchaseCredits(pack.qty)
                      if (url) window.location.href = url
                    } catch (err) {
                      setError(err.message || 'Failed to start purchase')
                      setBuyingCredits(null)
                    }
                  }}
                  disabled={buyingCredits !== null}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {buyingCredits === pack.qty
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</>
                    : <><CreditCard className="w-4 h-4" /> Buy Now</>}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="card">
              <h3 className="font-bold text-white mb-2">How does the free trial work?</h3>
              <p className="text-gray-300">
                New Basic plan subscribers get a 14-day free trial.
                After the trial, your Basic subscription continues at the regular price.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-white mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-300">
                Yes! Cancel your subscription anytime from your account settings. You'll retain access
                until the end of your billing period.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-white mb-2">What's the difference between Basic and Premium?</h3>
              <p className="text-gray-300">
                Premium adds phone number scanning, brand impersonation detection, community reviews,
                unlimited history, and email/SMS integration. Basic covers email scanning with AI analysis.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-white mb-2">How do credits work?</h3>
              <p className="text-gray-300">
                Each scan costs 1 credit. Basic plans include 300 credits/month and Premium includes 500.
                Credits reset monthly with your billing cycle. Need more? Purchase extra credits in packs of
                100 for $1 — purchased credits never expire.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-white mb-2">Is my data private?</h3>
              <p className="text-gray-300">
                Absolutely. We only store the email/phone you scan and the results. Message content is
                analyzed in real-time but not permanently stored. We never sell your data.
              </p>
            </div>

            <div className="card">
              <h3 className="font-bold text-white mb-2">How accurate is ScamView?</h3>
              <p className="text-gray-300">
                Our multi-signal approach combines AI analysis, DNS verification, web reputation checks,
                and domain intelligence to surface risk. We're constantly improving our detection models.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-16">
          <p className="text-gray-300 mb-4">Still have questions?</p>
          <button
            onClick={() => navigate('/about')}
            className="btn-secondary"
          >
            Learn More About ScamView
          </button>
        </div>
      </main>

      <Footer />
    </div>
  )
}
